import { useEffect, useRef } from 'react';

/**
 * Picker «Коммерческое предложение» (PR-H5a). Открывается с Главной при клике на
 * блок id=14 (prdBlockNumber=12, entry='quote') ПОСЛЕ прохождения paywall-гейта.
 * Предлагает 2 действия:
 *   - «Создать новый КП» → navigate('/calculator/quote?new=true') (через onCreateNew).
 *   - «Открыть сохранённое КП» → navigate('/calculator/quote/saved') (через onOpenSaved,
 *     PR-H5b Commit 1 — SavedQuotesListContainer browse screen).
 *
 * UI зеркалирует существующие модалки PaywallModal / UnavailableModal: центральный
 * overlay 50%, карточка bg-canvas radius 20, вертикальный стек кнопок (CLAUDE.md
 * «интерактивные кнопки только вертикальными списками»). Это модальный overlay, а
 * НЕ страница — layout shell (P-046) и VideoButton (P-040) не применяются.
 *
 * Design system: text-ink на всём тексте (P-039), палитра только через токены
 * (P-045: bg-canvas / bg-cta / border-accent / text-ink), min-h-tap (P-044),
 * никаких text-cta/text-accent на тексте кнопок (P-041).
 *
 * a11y per P-022: role="dialog", aria-modal, aria-labelledby связь с h2. Initial
 * focus на «Создать новый КП». Закрытие: overlay click, Escape, кнопка «Отмена».
 *
 * Если появится 3-й consumer этого модал-паттерна — рефакторим в shared `Modal`
 * примитив (см. правило «3rd consumer trigger» в BACKLOG B-049).
 */
export interface QuoteEntryPickerProps {
  readonly onCreateNew: () => void;
  readonly onOpenSaved: () => void;
  readonly onClose: () => void;
}

export function QuoteEntryPicker({ onCreateNew, onOpenSaved, onClose }: QuoteEntryPickerProps) {
  const createButtonRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose();
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  useEffect(() => {
    createButtonRef.current?.focus();
  }, []);

  return (
    <div
      onClick={onClose}
      style={{ position: 'fixed', inset: 0, zIndex: 100, padding: 20 }}
      className="flex items-center justify-center bg-black/50"
      data-testid="quote-entry-overlay"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="quote-entry-title"
        className="w-full bg-canvas text-center"
        style={{
          maxWidth: 360,
          borderRadius: 20,
          padding: 24,
          boxShadow: '0 16px 64px rgba(0,0,0,.4)',
        }}
      >
        <h2
          id="quote-entry-title"
          className="font-display text-h2 text-ink leading-snug"
          style={{ margin: '0 0 24px' }}
        >
          Коммерческое предложение
        </h2>

        <div className="flex flex-col gap-3">
          <button
            ref={createButtonRef}
            type="button"
            onClick={onCreateNew}
            aria-label="Создать новый КП"
            className={
              'w-full font-bold bg-cta text-ink border-b-2 border-cta-shadow ' +
              'min-h-tap-chip-lg rounded-2xl px-4 ' +
              'focus-visible:ring-2 focus-visible:ring-cta focus-visible:ring-offset-2 ' +
              'focus-visible:outline-none active:opacity-90 transition-opacity'
            }
            style={{ fontSize: 19 }}
          >
            Создать новый КП
          </button>

          <button
            type="button"
            onClick={onOpenSaved}
            aria-label="Открыть сохранённое КП"
            className={
              'w-full min-h-tap rounded-2xl border-[1.5px] border-accent bg-card ' +
              'px-4 py-3 text-ink font-medium ' +
              'focus-visible:ring-2 focus-visible:ring-cta focus-visible:outline-none ' +
              'active:opacity-90 transition-opacity'
            }
            style={{ fontSize: 17 }}
          >
            Открыть сохранённое КП
          </button>

          <button
            type="button"
            onClick={onClose}
            aria-label="Отмена"
            className={
              'w-full min-h-tap font-medium text-ink bg-transparent ' +
              'border-[1.5px] border-accent rounded-2xl px-4 py-3 ' +
              'focus-visible:ring-2 focus-visible:ring-cta focus-visible:outline-none ' +
              'active:opacity-90 transition-opacity'
            }
            style={{ fontSize: 17 }}
          >
            Отмена
          </button>
        </div>
      </div>
    </div>
  );
}
